import { GET, POST, DELETE } from '@/shared/api/requests';
import { apiClient } from '@/lib/api-client';
import { uploadPDF } from '@/features/pdf-editor/api/index';
import { Layout } from '../types/types';

/**
 * Minimal PDF page metadata required to render a PDF-based layout in
 * the interactive viewer. Returned by the layout-scoped pdf-info
 * endpoint, which (unlike the drawings PDFPage endpoint) is permissioned
 * against the layout's project rather than the PDF file's project — so
 * non-staff users in the layout's project can always read it.
 */
export interface LayoutPdfInfo {
  pdf_page_id: string;
  pdf_file_id: string;
  pdf_file_url: string | null;
  page_number: number;
  page_count: number;
  width: number;
  height: number;
  rotation: number;
  dpi: number;
}

/**
 * Fetch the minimal PDF metadata (page #, dimensions, rotation, dpi,
 * signed URL) for a PDF-based layout.
 */
export const getLayoutPdfInfo = async (
  layoutId: string | number
): Promise<LayoutPdfInfo> => {
  const response = await GET<void>(`/layouts/${layoutId}/pdf-info/`);
  return response.data as LayoutPdfInfo;
};

/** Same-origin authenticated raster used instead of PDF.js on constrained devices. */
export const getLayoutPdfRasterUrl = (
  layoutId: string | number,
): string =>
  apiClient.getUri({
    url: `/layouts/${layoutId}/pdf-raster/`,
    params: { scale: 1 },
  });

interface AttachPdfOptions {
  pageNumber?: number;
  cropRegion?: [number, number, number, number] | null;
  flippedHorizontal?: boolean;
  flippedVertical?: boolean;
}

/**
 * Attach a PDF page (and optional crop region) to a layout.
 * @param layoutId    - The layout ID
 * @param fileOrPdfId - Either a File object to upload or an existing PDFFile UUID
 * @param options     - Optional page number (default 1) and crop region [x, y, w, h]
 * @param projectId   - Required only when uploading a new File
 */
export const attachPdfToLayout = async (
  layoutId: string,
  fileOrPdfId: File | string,
  options: AttachPdfOptions = {},
  projectId?: number
): Promise<{ status: string; layout: Layout }> => {
  const {
    pageNumber = 1,
    cropRegion = null,
    flippedHorizontal = false,
    flippedVertical = false,
  } = options;

  let pdfFileId: string;

  if (fileOrPdfId instanceof File) {
    if (!projectId) {
      throw new Error('projectId is required when uploading a new PDF file');
    }
    const pdfFile = await uploadPDF(fileOrPdfId, projectId);
    pdfFileId = pdfFile.id;
  } else {
    pdfFileId = fileOrPdfId;
  }

  const response = await POST<{
    pdf_file_id: string;
    page_number: number;
    pdf_crop_region?: number[] | null;
    flipped_horizontal?: boolean;
    flipped_vertical?: boolean;
  }>(
    `/layouts/${layoutId}/attach-pdf/`,
    {
      pdf_file_id: pdfFileId,
      page_number: pageNumber,
      ...(cropRegion !== null ? { pdf_crop_region: cropRegion } : {}),
      flipped_horizontal: flippedHorizontal,
      flipped_vertical: flippedVertical,
    }
  );

  return response.data;
};

export const copyPdfLayoutToLayout = async (
  layoutId: string,
  sourceLayoutId: string,
  copyMarkers: boolean,
  options: Pick<AttachPdfOptions, 'flippedHorizontal' | 'flippedVertical'> = {}
): Promise<{ status: string; layout: Layout; copied_markers_count: number }> => {
  const {
    flippedHorizontal = false,
    flippedVertical = false,
  } = options;
  const response = await POST<{
    source_layout_id: string;
    copy_markers: boolean;
    flipped_horizontal?: boolean;
    flipped_vertical?: boolean;
  }>(
    `/layouts/${layoutId}/attach-pdf/`,
    {
      source_layout_id: sourceLayoutId,
      copy_markers: copyMarkers,
      flipped_horizontal: flippedHorizontal,
      flipped_vertical: flippedVertical,
    }
  );

  return response.data;
};

/**
 * Detach PDF (and crop region) from a layout.
 * @param layoutId - The layout ID
 */
export const detachPdfFromLayout = async (
  layoutId: string
): Promise<{ status: string; layout: Layout }> => {
  const response = await DELETE<void>(`/layouts/${layoutId}/attach-pdf/`);
  return response.data;
};
